<template>
  <div class="fill-height overflow-auto">
    <reference-field
      v-if="reference.studyNumber"
      data-testid="study-id-field"
      label="Study ID"
      class="mb-5"
    >
      {{ reference.displayStudyId }}
    </reference-field>
    <reference-field
      v-if="showConfidence"
      label="Relevance Confidence"
      class="mb-5"
    >
      <v-chip color="blue lighten-4 primary--text font-weight-bold">
        {{ confidencePercentage }}%
      </v-chip>
    </reference-field>
    <div class="d-flex justify-space-between">
      <reference-field
        data-testid="authors-field"
        label="Authors"
        class="mb-5 mr-5"
        :badge-content="authorMismatchCount"
      >
        <authors-reference-field
          v-if="hasAuthors || disableEdit"
          :authors="reference.publication.authors"
        />
        <template v-else>
          <add-missing-value-chip @click="$emit('edit', 'authors')" />
        </template>
      </reference-field>
      <slot name="actions"></slot>
    </div>
    <reference-field
      data-testid="year-field"
      label="Year"
      class="mb-5"
      :badge-content="yearMismatchCount"
    >
      <template v-if="hasYear || disableEdit">
        {{ year }}
      </template>
      <template v-else>
        <add-missing-value-chip @click="$emit('edit', 'year')" />
      </template>
    </reference-field>
    <reference-field
      data-testid="place-published-field"
      label="Place Published"
      class="mb-5"
      :badge-content="placePublishedMismatchCount"
    >
      <template v-if="hasPlacePublished || disableEdit">
        {{ placePublished }}
      </template>
      <template v-else>
        <add-missing-value-chip
          @click="$emit('edit', 'placePublished')"
        />
      </template>
    </reference-field>
    <reference-field
      data-testid="doi-field"
      label="DOI"
      class="mb-2"
      :badge-content="doiMismatchCount"
    >
      <div class="d-flex align-center">
        <a
          v-if="hasDoi"
          class="text-decoration-none primary--text"
          :href="doiLink"
          target="_blank"
        >
          {{ reference.publication.doi }}
        </a>
        <template v-else-if="disableEdit">-</template>
        <template v-else>
          <add-missing-value-chip @click="$emit('edit', 'doi')" />
        </template>
        <v-chip
          v-if="hasDoi && showFullTextReviewField && !hasFullTextPdf"
          x-small
          outlined
          color="primary"
          class="ml-2"
        >
          <v-icon x-small left>cloud_download</v-icon>
          PDF fetchable
        </v-chip>
      </div>
    </reference-field>

    <v-divider class="my-5" />

    <reference-field
      data-testid="tags-field"
      label="Tags"
      class="mb-5"
    >
      <reference-tag-chip-selector
        v-model="selectedTags"
        :available-tags="reviewTags"
        class="mt-2"
      />
      <action-button class="mt-4" @click="openTagSettingsModal">
        <v-icon left class="material-icons-outlined">
          settings
        </v-icon>
        Configure Tags
      </action-button>
    </reference-field>
    <reference-field
      v-if="showFullTextReviewField"
      label="Full Text PDF"
    >
      <div v-if="!hasFullTextPdf" class="d-flex align-center mb-2">
        <upload-full-text-pdf-controller
          class="flex-grow-0 mr-3"
          dense
          :reference="reference"
        />
        <v-tooltip bottom max-width="320">
          <template #activator="{ on }">
            <action-button
              :loading="fetchingPdf || pdfFetchInProgress"
              :disabled="!canFetchPdf || retryDisabled"
              v-on="on"
              @click="onFetchPdf"
            >
              <v-icon left class="material-icons-outlined"
                >picture_as_pdf</v-icon
              >
              Fetch PDF
            </action-button>
          </template>
          <span v-if="!canFetchPdf">
            Cannot fetch PDF: This reference is missing a DOI. Please
            add a DOI to enable automatic PDF fetching.
          </span>
          <span v-else>
            Attempt to auto-fetch a full-text PDF (if available) using
            the publication's metadata (e.g., DOI). This runs in the
            background and may take a minute.
          </span>
        </v-tooltip>
      </div>
      <div v-if="!hasFullTextPdf" class="mb-4">
        <div
          v-if="pdfFetchInProgress"
          class="d-flex align-center grey--text text--darken-1"
        >
          <v-progress-circular
            indeterminate
            size="16"
            width="2"
            class="mr-2"
          />
          Searching for a full-text PDF…
        </div>
        <div
          v-else-if="pdfFetchFailed && pdfFetchFailureReason"
          class="d-flex align-center"
          :class="`${pdfFetchFailureReason.color}--text text--darken-2`"
        >
          <v-icon
            small
            class="mr-1"
            :color="pdfFetchFailureReason.color"
          >
            {{ pdfFetchFailureReason.icon }}
          </v-icon>
          {{ pdfFetchFailureReason.message }}
          <v-spacer />
          <action-button
            v-if="pdfFetchFailureReason.showDoi"
            small
            text
            class="ml-2"
            @click="openDoi"
          >
            Open DOI
          </action-button>
          <action-button
            v-if="pdfFetchFailureReason.canRetry"
            small
            text
            class="ml-2"
            :disabled="retryDisabled"
            @click="onFetchPdf"
          >
            Try again
          </action-button>
        </div>
        <div v-else class="caption grey--text text--darken-1">
          Not all publishers provide direct PDF access. If we can't
          fetch one, try the DOI link or your institutional access.
        </div>
        <v-expansion-panels
          v-if="pdfFetchEventLogs.length > 0"
          flat
          class="mt-2"
        >
          <v-expansion-panel>
            <v-expansion-panel-header class="px-0 caption grey--text">
              View fetch history ({{ pdfFetchEventLogs.length }}
              {{
                pdfFetchEventLogs.length === 1
                  ? 'attempt'
                  : 'attempts'
              }})
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-timeline dense class="ml-n4 mt-2">
                <v-timeline-item
                  v-for="event in pdfFetchEventLogs"
                  :key="event.id"
                  :color="getEventColor(event.event)"
                  small
                >
                  <div class="caption">
                    <strong>{{ getEventTitle(event.event) }}</strong>
                    <div class="grey--text text--darken-1">
                      {{ formatEventDate(event.createdAt) }}
                    </div>
                    <div
                      v-if="event.payload && event.payload.error"
                      class="error--text text--darken-2 mt-1"
                    >
                      {{ event.payload.error }}
                    </div>
                    <div
                      v-if="
                        event.payload && event.payload.externalPdfUrl
                      "
                      class="grey--text text--darken-2 mt-1 text-truncate"
                      style="max-width: 300px"
                    >
                      Source: {{ event.payload.externalPdfUrl }}
                    </div>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>
      <div>
        <v-row class="mb-4" no-gutters>
          <action-button
            v-if="hasFullTextPdf"
            color="primary"
            class="mr-2"
            @click="$emit('download-full-text')"
          >
            <v-icon class="material-icons-outlined" left>
              download
            </v-icon>
            Download
          </action-button>
          <action-button
            v-if="hasFullTextPdf"
            color="error"
            @click="$emit('delete-full-text')"
          >
            <v-icon class="material-icons-outlined" left>
              delete
            </v-icon>
            Delete
          </action-button>
        </v-row>
        <v-alert
          v-if="showCacheHitMessage"
          type="success"
          dense
          class="caption"
          outlined
        >
          <v-icon small left>flash_on</v-icon>
          PDF fetched instantly from cache!
        </v-alert>
        <v-alert
          v-if="
            reference.fullTextPdfAutoFetched && !showCacheHitMessage
          "
          :key="reference.id"
          type="info"
          dense
          class="caption"
          outlined
        >
          We have automatically uploaded this PDF for you. If there
          are any issues, feel free to delete and upload a new file.
        </v-alert>
      </div>
    </reference-field>
  </div>
</template>

<script>
import AddMissingValueChip from '@/components/Common/AddMissingValueChip';
import ActionButton from '@/components/Common/ActionButton';
import ReferenceTagChipSelector from '@/components/References/ReferenceTagChipSelector';
import ReferenceField from '@/components/References/ReferenceField';
import AuthorsReferenceField from '@/components/References/AuthorsReferenceField';
import { Reference, Review } from '@/models';
import UploadFullTextPdfController from '../UploadFullTextPdfController/UploadFullTextPdfController.vue';

export default {
  name: 'ReferenceDetailFields',
  components: {
    ReferenceField,
    AddMissingValueChip,
    ActionButton,
    ReferenceTagChipSelector,
    UploadFullTextPdfController, // Should really emit events
    AuthorsReferenceField,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    reference: {
      type: Reference,
      required: true,
    },
    authorMismatchCount: {
      type: Number,
      default: 0,
    },
    yearMismatchCount: {
      type: Number,
      default: 0,
    },
    placePublishedMismatchCount: {
      type: Number,
      default: 0,
    },
    doiMismatchCount: {
      type: Number,
      default: 0,
    },
    disableEdit: {
      type: Boolean,
      default: false,
    },
    showFullTextReviewField: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'edit',
    'update-tags',
    'open-tag-settings',
    'delete-full-text',
    'download-full-text',
  ],
  data: () => ({
    currentTags: null,
    fetchingPdf: false,
    lastFetchRequestedAt: 0,
    showCacheHitMessage: false,
  }),
  computed: {
    selectedTags: {
      get() {
        return this.currentTags;
      },
      set(tags) {
        this.currentTags = [...tags];
        this.$emit('update-tags', this.currentTags);
      },
    },
    hasAuthors() {
      return (
        this.reference.publication.authors != null &&
        this.reference.publication.authors.length > 0
      );
    },
    hasPlacePublished() {
      return (
        this.reference.publication.placePublished != null &&
        this.reference.publication.placePublished.trim().length > 0
      );
    },
    placePublished() {
      return this.hasPlacePublished
        ? this.reference.publication.placePublished
        : '-';
    },
    hasDoi() {
      return (
        this.reference.publication.doi != null &&
        this.reference.publication.doi.trim().length > 0
      );
    },
    doiLink() {
      return this.reference.publication.doiLink();
    },
    hasYear() {
      return (
        this.reference.publication.year &&
        this.reference.publication.year > 0
      );
    },
    year() {
      return this.hasYear ? this.reference.publication.year : '-';
    },
    reviewTags() {
      return this.review.tags;
    },
    hasFullTextPdf() {
      return !!this.reference.fullTextPdfUri;
    },
    pdfFetchInProgress() {
      return this.reference.pdfFetchInProgress;
    },
    pdfFetchFailed() {
      return this.reference.pdfFetchFailed;
    },
    canFetchPdf() {
      // Check if reference has required metadata for fetch (DOI is required)
      return !!this.reference.publication?.doi;
    },
    retryDisabled() {
      const COOLDOWN_MS = 30000;
      return (
        Date.now() - this.lastFetchRequestedAt < COOLDOWN_MS ||
        this.pdfFetchInProgress
      );
    },
    pdfFetchFailureReason() {
      const job = this.reference.latestFetchFullTextJob;
      if (!job || !this.pdfFetchFailed) return null;

      switch (job.state) {
        case 'PDF_NOT_FOUND':
          return {
            icon: 'cloud_off',
            color: 'warning',
            message:
              'No direct PDF was found. Many publishers restrict downloads.',
            canRetry: true,
            showDoi: true,
          };
        case 'ERROR':
          return {
            icon: 'error_outline',
            color: 'error',
            message:
              job.error ||
              'Error fetching PDF. Please try again later.',
            canRetry: true,
            showDoi: false,
          };
        case 'TIMED_OUT':
          return {
            icon: 'schedule',
            color: 'warning',
            message:
              'PDF fetch timed out. The download may be too large.',
            canRetry: true,
            showDoi: true,
          };
        default:
          return {
            icon: 'error_outline',
            color: 'warning',
            message:
              'No direct PDF was found. Many publishers restrict downloads.',
            canRetry: true,
            showDoi: true,
          };
      }
    },
    pdfFetchEventLogs() {
      // Filter event logs for PDF fetch events
      const pdfFetchEvents = [
        'FULL_TEXT_PDF_FETCH_STARTED',
        'FULL_TEXT_PDF_FETCH_NOT_FOUND',
        'FULL_TEXT_PDF_FETCH_ERROR',
      ];
      return (
        this.reference.eventLogs?.filter((log) =>
          pdfFetchEvents.includes(log.event)
        ) || []
      ).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
    showConfidence() {
      return (
        !this.reference.isScreened() &&
        !!this.reference.autoScreeningClassification?.score
      );
    },
    confidencePercentage() {
      return Math.round(
        this.reference.autoScreeningClassification.score * 100
      );
    },
  },
  watch: {
    reference: {
      handler(newRef, oldRef) {
        if (newRef?.id !== oldRef?.id) {
          if (newRef) {
            this.currentTags = [...newRef.tags];
          } else {
            this.currentTags = [];
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    openTagSettingsModal() {
      this.$emit('open-tag-settings');
    },
    async onFetchPdf() {
      if (!this.canFetchPdf || this.retryDisabled) return;
      this.fetchingPdf = true;
      this.showCacheHitMessage = false;
      try {
        const result = await this.$store.dispatch(
          'fullTextPdf/requestAutoFetch',
          this.reference
        );
        this.lastFetchRequestedAt = Date.now();

        // Check if PDF was immediately available from cache
        if (result.fullTextPdfUri) {
          this.showCacheHitMessage = true;
          // Hide message after 5 seconds
          setTimeout(() => {
            this.showCacheHitMessage = false;
          }, 5000);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Error requesting PDF fetch', e);
      } finally {
        this.fetchingPdf = false;
      }
    },
    openDoi() {
      if (this.reference?.publication?.doi) {
        const href = this.reference.publication.doiLink();
        if (href) window.open(href, '_blank');
      }
    },
    getEventColor(event) {
      const colorMap = {
        FULL_TEXT_PDF_FETCH_STARTED: 'info',
        FULL_TEXT_PDF_FETCH_NOT_FOUND: 'warning',
        FULL_TEXT_PDF_FETCH_ERROR: 'error',
      };
      return colorMap[event] || 'grey';
    },
    getEventTitle(event) {
      const titleMap = {
        FULL_TEXT_PDF_FETCH_STARTED: 'Fetch started',
        FULL_TEXT_PDF_FETCH_NOT_FOUND: 'PDF not found',
        FULL_TEXT_PDF_FETCH_ERROR: 'Fetch error',
      };
      return titleMap[event] || event;
    },
    formatEventDate(date) {
      if (!date) return '';
      const eventDate = new Date(date);
      const now = new Date();
      const diffMs = now - eventDate;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60)
        return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24)
        return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7)
        return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
      return eventDate.toLocaleDateString();
    },
  },
};
</script>
